import type { SpriteCatalogIndex, SpriteLibrary } from './spriteTypes';

function fuzzyCharMatch(query: string, target: string): boolean {
  let qi = 0;
  for (let ti = 0; ti < target.length && qi < query.length; ti++) {
    if (target[ti] === query[qi]) qi++;
  }
  return qi === query.length;
}

function scoreEntry(query: string, entry: SpriteCatalogIndex): number {
  const q = query.toLowerCase();
  const name = entry.name.toLowerCase();
  const label = entry.label.toLowerCase();

  // Exact match
  if (name === q) return 200;

  // Prefix match on name
  if (name.startsWith(q)) return 150;

  // Exact substring in name
  if (name.includes(q)) return 100;

  // Word-prefix match (name split by - or _)
  const nameParts = name.split(/[-_]/);
  for (const part of nameParts) {
    if (part.startsWith(q)) return 60;
  }

  // Substring in label
  if (label.includes(q)) return 55;

  // Tag matches
  for (const tag of entry.tags) {
    const t = tag.toLowerCase();
    if (t === q) return 60;
    if (t.startsWith(q)) return 50;
    if (t.includes(q)) return 40;
  }

  // Category match
  if (entry.category.toLowerCase().includes(q)) return 20;

  // Fuzzy character-in-order fallback
  if (fuzzyCharMatch(q, name)) return 10;

  return 0;
}

export function fuzzySearchSprites(
  query: string,
  catalog: SpriteCatalogIndex[],
  libraryFilter: SpriteLibrary | 'all',
  maxResults = 200,
): SpriteCatalogIndex[] {
  // Filter by library
  const filtered = libraryFilter === 'all'
    ? catalog
    : catalog.filter(e => e.library === libraryFilter);

  // No query: return all (or capped)
  if (!query.trim()) {
    return maxResults > 0 ? filtered.slice(0, maxResults) : filtered;
  }

  // Score and sort
  const scored: Array<{ entry: SpriteCatalogIndex; score: number }> = [];
  for (const entry of filtered) {
    const score = scoreEntry(query, entry);
    if (score > 0) scored.push({ entry, score });
  }

  scored.sort((a, b) => b.score - a.score);
  const sorted = scored.map(s => s.entry);
  return maxResults > 0 ? sorted.slice(0, maxResults) : sorted;
}
